import { useEffect, useState } from "react";
import { BrandingStatus } from "../types";

export const DEFAULT_LOGO = "./cuscal-logo.svg";

/** Give up waiting for a merchant logo download and fall back to the default. */
const MERCHANT_LOGO_TIMEOUT_MS = 5000;

/**
 * Resolves the single logo to paint for a page load.
 *
 * Returns `null` while the answer is not final yet, so the caller can reserve the
 * space and paint nothing. Once `/intent` has resolved:
 *
 * - merchant logo present -> it is downloaded and decoded off-screen first, then
 *   returned, so the reveal is one clean paint instead of a progressive load
 * - no merchant logo, or the merchant logo fails to load -> the default logo
 *
 * The default logo is never requested while a merchant logo is expected, so a
 * merchant-branded page never fetches cuscal-logo.svg.
 */
export const useBrandLogo = (
  logoURL: string | undefined,
  brandingStatus: BrandingStatus
): string | null => {
  const [logoSrc, setLogoSrc] = useState<string | null>(null);

  useEffect(() => {
    if (brandingStatus !== "resolved") {
      setLogoSrc(null);
      return;
    }

    if (!logoURL) {
      setLogoSrc(DEFAULT_LOGO);
      return;
    }

    let cancelled = false;
    const image = new Image();

    const settle = (src: string) => {
      if (cancelled) return;
      // Stop any later callback from swapping the logo a second time.
      image.onload = null;
      image.onerror = null;
      window.clearTimeout(timeoutId);
      setLogoSrc(src);
    };

    const timeoutId = window.setTimeout(() => {
      console.warn("⚠️ Merchant logo took too long to load, using the default logo.");
      settle(DEFAULT_LOGO);
    }, MERCHANT_LOGO_TIMEOUT_MS);

    image.onload = () => {
      // decode() keeps the reveal on one frame; it is not supported everywhere
      // and can reject even for a usable image, so failures are ignored.
      const decoded = image.decode?.() ?? Promise.resolve();
      decoded.catch(() => undefined).then(() => settle(logoURL));
    };

    image.onerror = () => {
      console.warn("⚠️ Merchant logo failed to load, using the default logo.");
      settle(DEFAULT_LOGO);
    };

    image.src = logoURL;

    return () => {
      cancelled = true;
      image.onload = null;
      image.onerror = null;
      window.clearTimeout(timeoutId);
    };
  }, [logoURL, brandingStatus]);

  return logoSrc;
};
